import Konva from 'konva';
import useImage from 'use-image';
import { Group, Image as RasterImage } from 'react-konva';
import { useDispatch } from 'react-redux';
import { forwardRef, useEffect, useState } from 'react';
import { CustomMapStateType, setOriginalDistance } from 'store/slices/CustomMapSlice';
import CalibrationGroup from '../Group/CustomMapGroup/CalibrationGroup';

type Props = {
	customMap: CustomMapStateType['customMaps'][0];
	isSelected: boolean;
	onTransformEnd: (e: Konva.KonvaEventObject<Event>) => void;
	isCalibrating: boolean;
	onImageLoad: (id: number) => void;
	onDragEnd: (e: Konva.KonvaEventObject<DragEvent>) => void;
	isDraggable: boolean;
};

const CustomRasterImage = forwardRef<Konva.Group, Props>((props, ref) => {
	const { customMap, onTransformEnd, isSelected, isCalibrating, onImageLoad, onDragEnd, isDraggable } = props;
	const [img, status] = useImage(customMap.imageUrl, 'anonymous', 'origin');

	const [rulerPoints, setRulerPoints] = useState<Vector2d[]>([]);
	const [tempPoint, setTempPoint] = useState<{ x: number; y: number } | null>(null);
	const [distance, setDistance] = useState<number | null>(null);

	const dispatch = useDispatch();

	useEffect(() => {
		setRulerPoints([]);
		setTempPoint(null);
		setDistance(null);
	}, [isCalibrating]);

	useEffect(() => {
		if (status === 'loaded') {
			onImageLoad(customMap.id);
		}
	}, [customMap.id, onImageLoad, status]);

	function handlePointerDown(e: Konva.KonvaEventObject<PointerEvent>) {
		if (!isCalibrating) return;
		e.cancelBubble = true;
		const group = e.currentTarget.getParent();
		const pointer = group?.getRelativePointerPosition();
		if (!pointer) return;

		// const pointer = {
		// 	x: rawPointer.x / baseImageScale.x,
		// 	y: rawPointer.y / baseImageScale.y,
		// };

		// Reset if already completed
		if (rulerPoints.length === 2) {
			setRulerPoints([pointer]);
			setTempPoint(pointer);
			setDistance(null);
			return;
		}

		// First click
		if (rulerPoints.length === 0) {
			setRulerPoints([pointer]);
			setTempPoint(pointer);
			return;
		}

		// Second click
		if (rulerPoints.length === 1) {
			const newPoints = [rulerPoints[0], pointer];

			const pixelDistance = Math.hypot(
				newPoints[1].x - newPoints[0].x,
				newPoints[1].y - newPoints[0].y
			);

			setDistance(pixelDistance);
			setRulerPoints(newPoints);
			setTempPoint(null);
			dispatch(setOriginalDistance({ val: pixelDistance }));
		}
	}

	function handlePointerMove(e: Konva.KonvaEventObject<PointerEvent>) {
		if (!isCalibrating) return;
		e.cancelBubble = true;
		if (rulerPoints.length !== 1) return;

		const group = e.currentTarget.getParent();
		const pointer = group?.getRelativePointerPosition();
		if (!pointer) return;
		setTempPoint(pointer);
	}

	function handleImageHover(evt: Konva.KonvaEventObject<PointerEvent>, hover: 'In' | 'Out') {
		if (!isCalibrating) return;

		evt.cancelBubble = true;
		const stage = evt.currentTarget.getStage();
		if (!stage) return;
		stage.container().style.cursor = hover === 'In' ? 'crosshair' : 'default';
	}

	function handleDragMove(evt: Konva.KonvaEventObject<DragEvent>) {
		evt.cancelBubble = true;
		const node = evt.currentTarget;
		const stage = node.getStage();
		if (stage) stage.container().style.cursor = 'grabbing';
	}

	return (
		<Group
			ref={ref}
			x={customMap.x}
			y={customMap.y}
			rotation={customMap.rotation}
			offsetX={customMap.pixelWidth / 2}
			offsetY={customMap.pixelHeight / 2}
			scaleX={customMap.scaleX}
			scaleY={customMap.scaleY}
			visible={customMap.isVisible}
			draggable={isSelected && !isCalibrating && isDraggable}
			listening={isSelected}
			onDragMove={handleDragMove}
			onDragEnd={onDragEnd}
			onTransformEnd={onTransformEnd}
		>
			<RasterImage
				id={`custom-map-${customMap.id}`}
				image={img}
				opacity={customMap.opacity / 100}

				width={customMap.pixelWidth}
				height={customMap.pixelHeight}

				onPointerEnter={(e) => handleImageHover(e, 'In')}
				onPointerLeave={(e) => handleImageHover(e, 'Out')}

				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
			/>

			{isCalibrating && (
				<CalibrationGroup
					imageScaleX={customMap.scaleX}
					imageScaleY={customMap.scaleY}
					rulerPoints={rulerPoints}
					tempPoint={tempPoint}
					distance={distance}
					listening={false}
					name={'calibration-grp'}
				/>
			)}
		</Group>
	);
});

CustomRasterImage.displayName = 'CustomRasterImage';
export default CustomRasterImage;